import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import StepContainer from '../../components/StepContainer';
import Button from '../../components/Button';
import ApiErrorMessage from '../../components/ApiErrorMessage';
import { useBuildAudience } from '../../context/BuildAudienceContext';
import { useNavigate } from 'react-router-dom';
import { DEFAULTS, POLLING_CONFIG } from '../../config/constants';
import { API_URL, ENDPOINTS } from '../../config/api';
import { UI_TEXT, ERROR_MESSAGES } from '../../config/ui-text';
import { useFeatureAccess } from '../../context/subscription/useFeatureAccess';
import { useAudienceData } from '../../contexts/AudienceContext';

interface AudienceFinalizeProps {
  onNext: () => void;
  onBack: () => void;
}

const AudienceFinalize: React.FC<AudienceFinalizeProps> = ({
  onNext,
  onBack,
}) => {
  const navigate = useNavigate();
  const { buildAudienceData, updateBuildAudienceData } = useBuildAudience();
  const { isFreePlan } = useFeatureAccess();
  const { refreshAudiences } = useAudienceData();

  // Track submission state - submission happens from segments page now
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string>('');

  // Track progress for free users
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 100, status: '' });
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Check if we should start polling - submission already happened from segments page
  useEffect(() => {
    // This component is reached after submission, check user type and task status
    const currentUserType = isFreePlan ? 'free' : 'paid';
    setUserType(currentUserType);
    setIsSubmitted(true);

    // Check if we have a task to monitor from context
    const taskId = buildAudienceData.submissionTaskId;

    if (taskId && isFreePlan) {
      // Free user with task - start polling for the task that was just submitted
      setIsPolling(true);
      startProgressPolling(taskId);
    } else {
      // Paid user or no task - immediate completion
      // Dispatch event to notify header about submission
      window.dispatchEvent(new CustomEvent('audience-submitted'));
    }
  }, [isFreePlan, buildAudienceData.submissionTaskId]);

  // Progress polling function for free users
  const startProgressPolling = async (taskIdToTrack: string) => {
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}${ENDPOINTS.AUDIENCE.FLOW.TASK_STATUS}/${taskIdToTrack}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const taskData = await response.json();

        // Update progress state
        setProgress({
          current: taskData.current || 0,
          total: taskData.total || 100,
          status: taskData.status || ''
        });

        // Check if task completed
        if (taskData.state === 'SUCCESS') {
          clearInterval(intervalId);
          setPollInterval(null);
          setIsPolling(false);
          // Dispatch event to notify header about submission completion
          window.dispatchEvent(new CustomEvent('audience-submitted'));
          // Keep isSubmitted true to show final success message
        } else if (taskData.state === 'FAILURE') {
          clearInterval(intervalId);
          setPollInterval(null);
          setIsPolling(false);
          setApiError(`Audience generation failed: ${taskdata.error || data.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error polling task status:', error);
        // Continue polling on error, don't break the flow
      }
    }, POLLING_CONFIG.PROGRESS_INTERVAL_MS);

    setPollInterval(intervalId);

    // Clean up interval after timeout to prevent infinite polling
    setTimeout(() => {
      clearInterval(intervalId);
      setPollInterval(null);
      setIsPolling(false);
    }, POLLING_CONFIG.PROGRESS_TIMEOUT_MS);
  };

  // Cleanup polling interval on component unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  return (
    <StepContainer
      title=""
      className="animate-fadeIn flex items-center justify-center min-h-[70vh]"
    >
      <div className="max-w-3xl mx-auto space-y-8 w-full text-center">
        {/* API Error message */}
        {apiError && (
          <div className="mb-6">
            <ApiErrorMessage
              error={apiError}
              onDismiss={() => setApiError('')}
            />
          </div>
        )}

        {/* Success message when submitted */}
        {isSubmitted ? (
          <div className="space-y-6">
            <div className="flex justify-center mb-6">
              {userType === 'free' && isPolling ? (
                <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
              ) : (
                <CheckCircle className="w-12 h-12 text-green-500" />
              )}
            </div>

            {userType === 'free' ? (
              isPolling ? (
                // Progress phase for free users
                <div className="space-y-4">
                  <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
                    {UI_TEXT.PROGRESS.CREATING_AUDIENCE}
                  </h1>
                  <p className="text-base font-light text-gray-400 leading-relaxed">
                    {UI_TEXT.PROGRESS.PROGRESS_UPDATE}
                  </p>

                  {/* Simple progress bar */}
                  <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-1 mt-8">
                    <div
                      className="bg-primary h-1 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>

                  <p className="text-sm font-light text-gray-400 mt-4">
                    {progress.status || UI_TEXT.PROGRESS.GENERATING_PERSONAS}
                  </p>
                </div>
              ) : (
                // Success phase for free users - Typeform style
                <div className="space-y-6">
                  <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
                    {UI_TEXT.TITLES.AUDIENCE_CREATED}
                  </h1>
                  <p className="text-base font-light text-gray-400 leading-relaxed mb-8">
                    {UI_TEXT.SUBTITLES.COMPLETION_NOTICE_FREE}
                  </p>

                  <Button
                    variant="primary"
                    onClick={async () => {
                      // Refresh audiences to show the newly created one
                      await refreshAudiences();
                      // Navigate to simulation page
                      navigate("/simulate");
                      onNext();
                    }}
                    className="font-light"
                  >
                    {UI_TEXT.BUTTONS.START_SIMULATING}
                  </Button>
                </div>
              )
            ) : (
              // Paid users success - Typeform style
              <div className="space-y-6">
                <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
                  {UI_TEXT.TITLES.THANK_YOU}
                </h1>
                <p className="text-base font-light text-gray-400 leading-relaxed mb-8">
                  {UI_TEXT.SUBTITLES.COMPLETION_NOTICE}
                </p>

                <Button
                  variant="primary"
                  onClick={() => {
                    // Reset audience data before navigating
                    updateBuildAudienceData({
                      audienceName: '',
                      totalAudienceSize: DEFAULTS.AUDIENCE.SIZE,
                      segmentPercentages: {},
                      segments: []
                    });
                    // Use location state flag to force a clean reset
                    navigate("/", { state: { reset: true } });
                    onNext();
                  }}
                  className="font-light"
                >
                  {UI_TEXT.BUTTONS.BACK_TO_HOME}
                </Button>
              </div>
            )}
          </div>
        ) : (
          // Loading state while detecting submission status
          <div className="space-y-6">
            <div className="flex justify-center">
              <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
                {UI_TEXT.LOADING.PROCESSING}
              </h1>
              <p className="text-base font-light text-gray-400 leading-relaxed">
                {UI_TEXT.LOADING.TIME_ESTIMATE}
              </p>
            </div>
          </div>
        )}
      </div>
    </StepContainer>
  );
};

export default AudienceFinalize;